import { useEffect, type ReactNode } from 'react';
import { LegalLink } from '../../reservations/public/PublicFrame';
import type { PublicOrganization } from './api';
import styles from './Booking.module.css';

/**
 * `<meta name="robots">`: `index,follow` only when the organization allows it (PLAN §9.2), else
 * `noindex,nofollow`; the previous value comes back when the page unmounts.
 */
export function useRobots(indexable: boolean | undefined): void {
  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = meta === null;
    if (meta === null) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    const previous = meta.content;
    meta.content = indexable === true ? 'index,follow' : 'noindex,nofollow';
    return () => {
      if (!meta) return;
      if (created) meta.remove();
      else meta.content = previous;
    };
  }, [indexable]);
}

export interface BookingFrameProps {
  organization?: PublicOrganization | null | undefined;
  indexable?: boolean | undefined;
  children: ReactNode;
}

/**
 * The lean public frame of the booking page (Buchungsseite 01–10): 68 px header „DAMOKLES · Termin buchen ·
 * Zeitzone Europe/Berlin", the centred content, the footer with the organization's legal links and the
 * data sentence. No session, no topbar. Below 768 px the header shrinks to the phone frames' bar.
 */
export function BookingFrame({ organization, indexable, children }: BookingFrameProps) {
  useRobots(indexable);
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.wordmark}>DAMOKLES</span>
        <span className={styles.divider} aria-hidden="true" />
        <span className={styles.context}>Termin buchen</span>
        <span className={styles.timezone}>Zeitzone Europe/Berlin</span>
      </header>
      <main className={styles.main}>{children}</main>
      <footer className={styles.footer}>
        {organization?.imprintUrl ? (
          <LegalLink url={organization.imprintUrl} title="Impressum" className={styles.legal} />
        ) : null}
        {organization?.privacyUrl ? (
          <LegalLink url={organization.privacyUrl} title="Datenschutz" className={styles.legal} />
        ) : null}
        <span className={styles.footerNote}>Ihre Daten werden nur für diesen Termin verwendet.</span>
      </footer>
    </div>
  );
}
